export type Role = 'admin' | 'ra' | 'qa' | 'design' | 'viewer';
export const ALL_ROLES: Role[] = ['admin', 'ra', 'qa', 'design', 'viewer'];
export type OrgStatus = 'pending_team' | 'active' | 'suspended' | 'provisioning_failed' | 'offboarded';

export interface Identity {
  user: {
    id: string;
    email: string;
    fullName: string | null;
    isVaultSuperuser: boolean;
    teamId: string | null;
    teamRole: string | null;
  };
  superAdmin: boolean;
  state: 'member' | 'suspended' | 'member_disabled' | 'pending_invite' | 'org_pending' | 'no_org';
  org?: {
    id: string;
    name: string;
    slug: string;
    status: OrgStatus;
    settings: { locale: 'en' | 'ko'; timezone: string; requireFourEyes?: boolean; ocrPositions?: boolean };
    evidenceRootDirectoryId: string | null;
  };
  roles: Role[];
  pendingInvites: { teamId: string; orgId: string; orgName: string }[];
  homeOrgId: string | null;
  orgs: { id: string; name: string; slug: string; status: OrgStatus; home: boolean }[];
  supportMode: boolean;
}
export interface ProvisionStep {
  id: 'P1' | 'P2' | 'P3' | 'P4' | 'P5' | 'P6';
  status: 'ok' | 'skipped' | 'failed';
  at: string;
  result?: Record<string, unknown>;
  error?: string;
}
export interface Org {
  id: string;
  name: string;
  slug: string;
  status: OrgStatus;
  adminEmail: string;
  adminUserId?: string | null;
  contract: { ref: string; endsAt?: string | null; notes?: string | null };
  vaultsageTeamId?: string | null;
  subscription?: { platform: string; tier: number; maxSeats: number; expiresAt: string | null } | null;
  provisioning: { steps: ProvisionStep[]; failedReason?: string | null; lastRunAt?: string };
  evidenceRootDirectoryId?: string | null;
  createdAt: string;
  memberCount?: number;
  inviteCount?: number;
  failed?: string | null;
}
export interface MemberRow {
  email: string;
  fullName: string | null;
  vaultUserId: string | null;
  vaultsageMemberId: string | null;
  teamStatus: string | null;
  teamRole: string | null;
  roles: Role[];
  status: 'active' | 'disabled' | 'invited' | 'not_joined';
  joinedAt: string | null;
  invitedAt: string | null;
  canResend: boolean;
}

export type DocStatus = 'uploaded' | 'verifying' | 'pass' | 'not_compliant' | 'failed';
export type DocKind = 'label_artwork' | 'label_copy' | 'formulation' | 'sfp' | 'evidence' | 'other';
export const DOC_KINDS: DocKind[] = [
  'label_artwork',
  'label_copy',
  'formulation',
  'sfp',
  'evidence',
  'other',
];
export interface Product {
  id: string;
  name: string;
  description: string;
  dosageForm?: string | null;
  servingSize?: { amount: number; unit: string } | null;
  servingsPerContainer?: number | null;
  launchTargetDate?: string | null;
  startDate: string;
  status: 'active' | 'archived';
  directoryId: string | null;
  reportsDirectoryId: string | null;
  documentCounts: Record<DocStatus, number>;
  documentExcluded?: number;
  currentFormulationVersion: number | null;
  createdAt: string;
}
export interface Document {
  id: string;
  name: string;
  fileName: string;
  ext: string;
  mime: string;
  bytes: number;
  vaultsageFileId: string;
  vaultsageProcessing: string;
  kind: DocKind;
  taskKey: string | null;
  status: DocStatus;
  supersedesDocId: string | null;
  supersededByDocId: string | null;
  latestCheckId?: string | null;
  activeCheckId?: string | null;
  latestResult?: { jurisdictions?: string[]; kbVersion: string } | null;
  approval: {
    state: 'none' | 'submitted' | 'approved' | 'changes_requested';
    submittedBy?: string;
    verifiedBy?: string;
    verifierRole?: string;
    decisionNote?: string | null;
  };
  uploadedBy: string;
  uploadedByEmail: string;
  uploadedAt: string;
  /** soft tag: left out of the verification counts, never deleted */
  excluded?: boolean;
  excludedByEmail?: string | null;
  excludedAt?: string | null;
  excludedReason?: string | null;
}
export interface Ingredient {
  name: string;
  commonName?: string | null;
  casNumber?: string | null;
  amountPerServing: number;
  unit: string;
  form?: string | null;
  function: 'active' | 'excipient';
  source?: string | null;
  nutrient?: string | null;
  factor?: number | null;
}
export interface NutrientMapping {
  id: string;
  alias: string;
  canonicalNutrient: string;
  factor: number;
  notes?: string | null;
  source: 'system' | 'org';
  orgId?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface CanonicalNutrient {
  name: string;
  unit: string;
  group: 'macronutrient' | 'vitamin' | 'mineral' | 'other';
  dvAdults: number;
  b2Order: number;
}
export interface Formulation {
  version: number;
  ingredients: Ingredient[];
  sourceDocId: string | null;
  note: string | null;
  createdBy: string;
  createdAt: string;
}
export interface Task {
  key: string;
  phase: string;
  workstream: string;
  title: string;
  owner: string;
  ownerRole: Role | null;
  gate: string;
  predecessors: string[];
  plannedStart: string;
  plannedEnd: string;
  status: 'not_started' | 'in_progress' | 'blocked' | 'in_review' | 'approved' | 'done';
  documentIds: string[];
  actionable: boolean;
}

export interface Finding {
  id: string;
  jurisdictionId?: 'us-fda' | 'kr-mfds';
  source: 'rule' | 'ai';
  subject: string;
  severity: 'critical' | 'major' | 'info' | 'ok';
  finding: string;
  citation: { kbFile: string; section: string; url?: string };
  fix: { what: string; suggestedText?: string | null };
  confidence: number;
  disposition: 'open' | 'accepted' | 'overridden';
  humanNote?: string | null;
  quote?: string | null;
  missing?: boolean;
  category?: string;
  location?: { page: number; bbox?: [number, number, number, number]; source: string; matchScore: number };
}
export interface Check {
  currentJurisdiction?: 'us-fda' | 'kr-mfds' | null;
  jurisdictions?: ('us-fda' | 'kr-mfds')[];
  regulatoryProfiles?: {
    'kr-mfds'?: { productClass: string; context: 'label' | 'advertising'; servingsPerDay?: number };
  };
  countryResults?: Record<
    string,
    {
      status: 'done' | 'failed';
      resultStatus: 'pass' | 'not_compliant' | null;
      coverageComplete: boolean;
      kbVersion: string;
      error: string | null;
    }
  >;
  id: string;
  type: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  stage?: 'processing' | 'positions' | 'rules' | 'claims' | 'label' | 'report' | null;
  startedAt?: string | null;
  scope?: string[];
  input: { formulationVersion?: number; docId?: string | null };
  positions?: {
    provider: string;
    pages: { page: number; hasTextLayer: boolean; wordCount: number }[];
    visionPagesBilled: number;
  } | null;
  kbVersion: string;
  promptVersion: string | null;
  result: {
    overall: 'green' | 'amber' | 'red';
    status: 'pass' | 'not_compliant';
    counts: Record<string, number>;
    items: Finding[];
    aiNotes: string;
  } | null;
  report: {
    masterFileId: string | null;
    orgFileId: string | null;
    orgCopyPending: boolean;
    fileName: string | null;
  } | null;
  metrics: { aiLatencyMs?: number; aiAttempts?: number; totalMs?: number } | null;
  requestedByEmail: string;
  createdAt: string;
  finishedAt: string | null;
  error: string | null;
}
